<template>
  <b-modal
    :id="modalId"
    size="lg"
    footer-class="flex-nowrap"
    centered
    no-close-on-backdrop
    @show="beimOeffnen"
  >
    <template #modal-header>
      <h3>{{ familie ? t('titelBearbeiten') : t('titelNeu') }}</h3>
    </template>

    <template #default>
      <div class="mx-4 my-3 schrift-familie">
        <b-row>
          <b-col cols="12" md="6" class="mb-3">
            <label class="mb-1" for="schrift-familie-name">{{ t('name') }}</label>
            <b-form-input
              id="schrift-familie-name"
              v-model="name"
              :state="name === '' ? null : nameOk"
              :placeholder="t('namePlatzhalter')"
            />
            <b-form-invalid-feedback>{{ nameFehler }}</b-form-invalid-feedback>
          </b-col>
          <b-col cols="12" md="6" class="mb-3">
            <label class="mb-1">{{ t('dateien') }}</label>
            <div
              class="ablage"
              :class="{ aktiv: ziehen }"
              role="button"
              tabindex="0"
              @click="$refs.datei.click()"
              @keydown.enter.prevent="$refs.datei.click()"
              @dragover.prevent="ziehen = true"
              @dragleave.prevent="ziehen = false"
              @drop.prevent="ablegen"
            >
              <span v-if="laedt">{{ t('laedt', { n: laedt }) }}</span>
              <span v-else>{{ t('ablage') }}</span>
            </div>
            <input
              ref="datei"
              type="file"
              class="d-none"
              multiple
              accept=".woff,.woff2"
              @change="dateienGewaehlt"
            >
          </b-col>
        </b-row>

        <div v-if="meldung" class="meldung mb-3">{{ meldung }}</div>

        <table v-if="schnitte.length" class="schnitte mb-2">
          <thead>
            <tr>
              <th>{{ t('probe') }}</th>
              <th>{{ t('staerke') }}</th>
              <th>{{ t('stil') }}</th>
              <th>{{ t('datei') }}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            <tr v-for="(s, i) in schnitte" :key="i" :class="{ doppelt: doppelt.includes(i) }">
              <td>
                <span class="probe" :style="probeStil(s)">{{ name || t('probeText') }}</span>
              </td>
              <td>
                <b-form-select v-model="s.fontWeight" size="sm" :options="staerkeOptionen" />
              </td>
              <td>
                <b-form-select v-model="s.style" size="sm" :options="stilOptionen" />
              </td>
              <td class="dateiname">{{ dateinamen(s) }}</td>
              <td>
                <b-icon
                  icon="trash"
                  class="pointer"
                  :aria-label="t('entfernen')"
                  @click="schnitte.splice(i, 1)"
                />
              </td>
            </tr>
          </tbody>
        </table>
        <b-form-text v-if="schnitte.length">{{ doppelt.length ? t('doppelt') : t('erkannt') }}</b-form-text>
        <b-form-text v-else>{{ t('leer') }}</b-form-text>

        <b-link class="d-inline-block mt-3" @click="erweitert = !erweitert">
          {{ t('erweitert') }} {{ erweitert ? '▾' : '▸' }}
        </b-link>
        <b-row v-if="erweitert" class="mt-2">
          <b-col cols="12" md="6">
            <label class="mb-1" for="schrift-familie-laden">{{ t('ladeverhalten') }}</label>
            <b-form-select id="schrift-familie-laden" v-model="fontDisplay" :options="ladeOptionen" />
            <b-form-text>{{ t('ladeverhaltenHilfe') }}</b-form-text>
          </b-col>
        </b-row>
      </div>
    </template>

    <template #modal-footer>
      <b-button class="p-3 border-right-light-grey" block @click="$bvModal.hide(modalId)">
        {{ $t('buttons.cancel') }}
      </b-button>
      <b-button variant="primary" class="p-3" block :disabled="!speicherbar" @click="speichern">
        {{ $tc('modals.schriftFamilie.speichern', schnitte.length, { n: schnitte.length }) }}
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import Auth from '@aws-amplify/auth';
import GetGroupObjectByGroupId from '@/graphQlQueries/queries/getGroupObjectByGroupId';
import { TYPE_WEIGHTS } from '@/editor-v2/utils/ciTokens';
import {
  ausDateiname, dateiBasis, dateiTyp, familienNameGueltig, schnitteBilden, VARIABEL,
} from '@/components/settings/eigeneSchriften';
import { schriftHochladen, familieSpeichern } from '@/components/settings/schriftHochladen';

// Preview faces by file url: each row shows its own file, whatever weight is picked.
const vorschau = new Map();

export default {
  name: 'ModalSchriftFamilie',
  props: {
    modalId: { type: String, required: true },
    groupId: { type: Number, required: true },
    // { name, schnitte } to edit or extend a family; null for a new one.
    familie: { type: Object, default: null },
    // Names of the account's other families: a second family must not take one.
    vorhandeneNamen: { type: Array, default: () => [] },
  },
  data: () => ({
    name: '',
    schnitte: [],
    fontDisplay: 'swap',
    erweitert: false,
    ziehen: false,
    laedt: 0,
    meldung: '',
    speichert: false,
    groupSlug: null,
  }),
  apollo: {
    groupSlug: {
      query: GetGroupObjectByGroupId,
      variables() { return { groupId: this.groupId }; },
      update: (data) => (data.groups[0] ? data.groups[0].slug : null),
    },
  },
  computed: {
    nameOk() {
      return familienNameGueltig(this.name) && !this.nameVergeben;
    },
    nameVergeben() {
      const eigener = this.familie && this.familie.name;
      return this.name !== eigener && this.vorhandeneNamen.includes(this.name);
    },
    nameFehler() {
      return this.nameVergeben ? this.t('nameVergeben') : this.t('nameUngueltig');
    },
    doppelt() {
      const schluessel = this.schnitte.map((s) => `${s.fontWeight}|${s.style}`);
      return schluessel.map((k, i) => (schluessel.indexOf(k) !== i ? i : -1)).filter((i) => i >= 0);
    },
    speicherbar() {
      return this.nameOk && this.schnitte.length > 0 && !this.doppelt.length && !this.laedt && !this.speichert;
    },
    staerkeOptionen() {
      return [
        // Same weight names as the typography table ("400 (Regular)").
        ...TYPE_WEIGHTS.map((w) => ({
          value: String(w),
          text: `${w} (${this.$t(`settings.project.corporateIdentity.typo.staerkeName.${w}`)})`,
        })),
        { value: VARIABEL, text: this.t('variabel') },
      ];
    },
    stilOptionen() {
      return [{ value: 'normal', text: this.t('normal') }, { value: 'italic', text: this.t('kursiv') }];
    },
    ladeOptionen() {
      return ['swap', 'fallback', 'optional', 'block', 'auto'].map((v) => ({ value: v, text: this.t(`laden_${v}`) }));
    },
  },
  methods: {
    t(key, params) {
      return this.$t(`modals.schriftFamilie.${key}`, params);
    },
    beimOeffnen() {
      const f = this.familie;
      this.name = f ? f.name : '';
      this.schnitte = f ? f.schnitte.map((s) => ({
        fontWeight: String(s.fontWeight),
        style: s.style === 'italic' ? 'italic' : 'normal',
        fontFiles: [...(s.fontFiles || [])],
        fallbackFont: s.fallbackFont,
      })) : [];
      const display = f && f.schnitte[0] && String(f.schnitte[0].fontDisplay || '').toLowerCase();
      this.fontDisplay = ['swap', 'fallback', 'optional', 'block', 'auto'].includes(display) ? display : 'swap';
      this.erweitert = false;
      this.meldung = '';
      this.speichert = false;
    },
    dateienGewaehlt(e) {
      this.hinzufuegen([...e.target.files]);
      e.target.value = '';
    },
    ablegen(e) {
      this.ziehen = false;
      this.hinzufuegen([...e.dataTransfer.files]);
    },
    async hinzufuegen(dateien) {
      const fonts = dateien.filter((d) => dateiTyp(d.name));
      this.meldung = fonts.length < dateien.length ? this.t('falscherTyp') : '';
      if (!fonts.length || !this.groupSlug) return;
      if (!this.name) {
        this.name = ausDateiname(fonts[0].name).familie.replace(/[^0-9A-Za-z_ -]/g, '').trim();
      }
      let author = null;
      try {
        author = (await Auth.currentSession()).getIdToken().payload.email;
      } catch (err) {
        author = null;
      }
      this.laedt = fonts.length;
      const geladen = await Promise.all(fonts.map((file) => schriftHochladen({
        apollo: this.$apollo, file, groupId: this.groupId, groupSlug: this.groupSlug, author,
      }).catch((err) => {
        console.error(err);
        return null;
      }).finally(() => { this.laedt -= 1; })));
      if (geladen.includes(null)) this.meldung = this.t('uploadFehler');
      schnitteBilden(geladen.filter(Boolean)).forEach((neu) => {
        // Only the WOFF/WOFF2 twin of a file already listed joins its row; anything else is a new row.
        const basis = dateiBasis(neu.fontFiles[0].url);
        const da = this.schnitte.find((s) => s.fontFiles.some((f) => dateiBasis(f.url) === basis));
        if (!da) {
          this.schnitte.push(neu);
          return;
        }
        const typen = neu.fontFiles.map((f) => f.filetype);
        da.fontFiles = [...da.fontFiles.filter((f) => !typen.includes(f.filetype)), ...neu.fontFiles];
      });
    },
    dateinamen(s) {
      return s.fontFiles
        .map((f) => decodeURIComponent(f.url).split('/').pop().split('?')[0].replace(/-\d{10,}(?=\.)/, ''))
        .join(', ');
    },
    probeStil(s) {
      const datei = s.fontFiles.find((f) => f.filetype === 'font/woff2') || s.fontFiles[0];
      if (!datei || typeof FontFace === 'undefined') return {};
      if (!vorschau.has(datei.url)) {
        const familie = `wm-vorschau-${vorschau.size}`;
        vorschau.set(datei.url, familie);
        const face = new FontFace(familie, `url("${datei.url}")`);
        document.fonts.add(face);
        face.load().catch(() => {});
      }
      return { fontFamily: `'${vorschau.get(datei.url)}', sans-serif` };
    },
    async speichern() {
      this.speichert = true;
      try {
        const settings = await familieSpeichern({
          apollo: this.$apollo,
          groupId: this.groupId,
          alterName: this.familie ? this.familie.name : null,
          name: this.name,
          schnitte: this.schnitte.map((s) => ({ ...s, fontDisplay: this.fontDisplay })),
        });
        this.$emit('gespeichert', settings, this.name);
        this.$bvModal.hide(this.modalId);
      } catch (err) {
        console.error(err);
        this.meldung = this.t('speicherFehler');
        this.speichert = false;
      }
    },
  },
};
</script>

<style scoped>
.ablage {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 38px;
  padding: 6px 12px;
  border: 1px dashed currentColor;
  border-radius: 4px;
  opacity: 0.85;
  cursor: pointer;
  text-align: center;
}

.ablage.aktiv,
.ablage:hover {
  opacity: 1;
  border-style: solid;
}

.schnitte {
  width: 100%;
}

.schnitte th {
  font-weight: 400;
  font-size: 0.8rem;
  opacity: 0.7;
  padding: 4px 6px;
}

.schnitte td {
  padding: 6px;
  vertical-align: middle;
  border-top: 1px solid rgba(128, 128, 128, 0.25);
}

.schnitte .probe {
  font-size: 1.25rem;
  white-space: nowrap;
}

.schnitte .dateiname {
  font-size: 0.75rem;
  opacity: 0.7;
  word-break: break-all;
}

.schnitte tr.doppelt td {
  background: rgba(220, 83, 83, 0.12);
}

.meldung {
  color: #dc5353;
}

.pointer {
  cursor: pointer;
}
</style>
